import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Scale,
  Ruler,
  Target,
  Activity,
  Loader2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    country_code: ''
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const [customerRes, statsRes] = await Promise.all([
          customersAPI.getOne(id),
          customersAPI.getStats(id)
        ]);
        setCustomer(customerRes.data);
        setStats(statsRes.data);
        setFormData({
          name: customerRes.data.name || '',
          phone_number: customerRes.data.phone_number || '',
          country_code: customerRes.data.country_code || ''
        });
      } catch (error) {
        toast.error('Failed to fetch customer details');
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await customersAPI.update(id, formData);
      toast.success('Customer updated successfully');
    } catch (error) {
      toast.error('Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="text-center py-20">
          <p className="text-stone-500">Customer not found</p>
        </div>
      </Layout>
    );
  }

  // Prepare BMI chart data
  const bmiData = customer.bmi?.map((record) => ({
    date: new Date(record.date_time).toLocaleDateString(),
    weight: record.weight,
    height: record.height
  })) || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="customer-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-stone-800">
              {customer.name || 'Unnamed Customer'}
            </h1>
            <p className="text-stone-500">User ID: #{customer.user_id}</p>
          </div>
          <Badge variant={customer.status === 'active' ? 'default' : 'secondary'}>
            {customer.status}
          </Badge>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health">Health Data</TabsTrigger>
            <TabsTrigger value="diet" data-testid="tab-diet">Diet Plans</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">Statistics</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info Card */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10"
                        data-testid="customer-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input value={customer.email} disabled className="pl-10 bg-muted" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        value={formData.phone_number}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        className="pl-10"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-customer-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>

              {/* Account Info Card */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-4">Account Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-stone-500">Date of Birth</span>
                    <span className="text-stone-800 font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {customer.date_of_birth || 'Not set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-stone-500">Premium Member</span>
                    <Badge className={customer.premium_member ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}>
                      {customer.premium_member ? 'Premium' : 'Free'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-stone-500">AI Plan</span>
                    <Badge className={customer.ai_plan ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}>
                      {customer.ai_plan ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-stone-500">Discount Code</span>
                    <span className="text-stone-800 font-mono">{customer.discount_code || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-stone-500">Referral Count</span>
                    <span className="text-stone-800 font-medium">{customer.referral_count || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Health Data Tab */}
          <TabsContent value="health" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Survey Data */}
              {customer.survey && (
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4">Health Profile</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Ruler className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">Height</p>
                        <p className="font-semibold text-stone-800">
                          {customer.survey.height_cm || '-'} {customer.survey.height_unit || 'cm'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Scale className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">Weight</p>
                        <p className="font-semibold text-stone-800">
                          {customer.survey.weight_kg || '-'} {customer.survey.weight_unit || 'kg'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">Target Weight</p>
                        <p className="font-semibold text-stone-800">
                          {customer.survey.target_weight || '-'} kg
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">Lifestyle</p>
                        <p className="font-semibold text-stone-800 capitalize">
                          {customer.survey.lifestyle || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* BMI Chart */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-4">Weight History</h3>
                {bmiData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={bmiData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                        <XAxis dataKey="date" stroke="#A8A29E" fontSize={12} />
                        <YAxis stroke="#A8A29E" fontSize={12} />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #E7E5E4',
                            borderRadius: '8px'
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="weight"
                          stroke="#10B981"
                          strokeWidth={2}
                          dot={{ fill: '#10B981' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-stone-400">
                    No BMI records found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Diet Plans Tab */}
          <TabsContent value="diet" className="space-y-6">
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-stone-800 mb-4">Diet Plan Details</h3>
              {customer.moderate_relax ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600">{customer.moderate_relax.calories || 0}</p>
                    <p className="text-stone-500 text-sm">Calories</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <p className="text-2xl font-bold text-amber-600">{customer.moderate_relax.protein || 0}g</p>
                    <p className="text-stone-500 text-sm">Protein</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">{customer.moderate_relax.carbs || 0}g</p>
                    <p className="text-stone-500 text-sm">Carbs</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <p className="text-2xl font-bold text-purple-600">{customer.moderate_relax.fat || 0}g</p>
                    <p className="text-stone-500 text-sm">Fat</p>
                  </div>
                </div>
              ) : (
                <p className="text-stone-400 text-center py-8">No diet plan configured</p>
              )}
            </div>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <p className="text-4xl font-bold text-emerald-600">{stats?.diet_intake_count || 0}</p>
                <p className="text-stone-500 mt-2">Total Food Entries</p>
              </div>
              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <p className="text-4xl font-bold text-blue-600">{stats?.bmi_records?.length || 0}</p>
                <p className="text-stone-500 mt-2">BMI Records</p>
              </div>
              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <p className="text-4xl font-bold text-amber-600">{stats?.water_intake_count || 0}</p>
                <p className="text-stone-500 mt-2">Water Entries</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CustomerDetailPage;
